import { useAuiState } from "@assistant-ui/react";
import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAccessGuard } from "@/hooks/use-access-guard";
import { ComposerModelPicker } from "@/routes/dashboard/components/assistant/composer-model-picker";
import { SearchEngineSelector } from "@/routes/dashboard/components/assistant/search-engine-picker";
import { Thread } from "@/routes/dashboard/components/assistant/thread";
import { HomeHeading } from "@/routes/dashboard/components/home/home-heading";
import { RecentActivity } from "@/routes/dashboard/components/home/recent-activity";
import { WebBar } from "@/routes/dashboard/components/home/web-bar";
import { AccessBlocked } from "@/shared/access-guard";

const HomeWelcome: FC = () => <HomeHeading />;

/** Recent chats and searches, narrowed to matches as the bar is typed into. */
const HomeFooter: FC = () => {
  const text = useAuiState((s) => s.composer.text);
  return <RecentActivity query={text} />;
};

/** Signed out: web search and URLs work; asking the AI needs an account. */
function SignedOutHome() {
  const [value, setValue] = useState("");
  return (
    <div className="flex h-full overflow-y-auto">
      <div className="mx-auto flex w-full max-w-190 flex-col px-4 pt-[18vh] pb-10">
        <HomeHeading />
        <div className="border-foreground focus-within:border-ring border transition-colors">
          <WebBar value={value} onValueChange={setValue} />
          <div className="border-border flex items-center justify-between gap-3 border-t px-3.5 py-3 text-sm">
            <span className="text-muted-foreground">Sign in to ask the AI and keep chats.</span>
            <Link
              to="/auth/signin"
              className="bg-primary text-primary-foreground hover:bg-brand hover:text-brand-foreground shrink-0 px-3 py-1.5 font-medium transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
        <div className="text-muted-foreground flex items-center gap-2 pt-3 text-xs">
          <SearchEngineSelector />
        </div>
        <RecentActivity types={["web"]} query={value} />
      </div>
    </div>
  );
}

export function Dashboard() {
  const { isAllowed, reason } = useAccessGuard();
  if (reason?.type === "unauthenticated") {
    return <SignedOutHome />;
  }

  if (!isAllowed) {
    return <AccessBlocked reason={reason as Exclude<typeof reason, null>} />;
  }

  return (
    <Thread
      components={{ Welcome: HomeWelcome, NewChatFooter: HomeFooter }}
      composerExtras={
        <div className="flex min-w-0 items-center gap-0.5">
          <SearchEngineSelector />
          <ComposerModelPicker />
        </div>
      }
    />
  );
}
